<template>
  <div>
    <v-card rounded="lg" outlined>
      <v-card-title class="text-h6 font-weight-medium py-4">
        <v-icon left color="primary">mdi-account-group</v-icon>
        Metas por Vendedor
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="visibleItems"
          item-key="unique_key"
          class="elevation-1"
          :loading="loading"
          loading-text="Carregando metas por vendedor..."
          :no-data-text="loading ? 'Carregando dados...' : 'Nenhum vendedor encontrado com metas para o período.'"
        >
          <template v-slot:no-data>
            <v-alert :value="true" type="info" class="mt-4">
              {{ loading ? 'Carregando dados de metas consolidadas...' : 'Nenhum vendedor encontrado com metas para o período selecionado.' }}
            </v-alert>
          </template>

          <template v-slot:item.expand="{ item }">
            <v-btn
              v-if="item.is_total_row"
              small
              icon
              @click="toggleExpand(item.user_id)"
            >
              <v-icon small>{{ expandedSellers.includes(item.user_id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            </v-btn>
            <span v-else class="pl-4"></span>
          </template>

          <template v-slot:item.s_name="{ item }">
            <div :class="{ 'font-weight-bold': item.is_total_row, 'pl-6': item.is_detail_row }">
              {{ item.s_name != null && item.s_name !== '' ? item.s_name : '-' }}
            </div>
          </template>
          <template v-slot:item.modality_name="{ item }">
            <div class="text-left">{{ item.modality_name != null && item.modality_name !== '' ? item.modality_name : '-' }}</div>
          </template>

          <template v-slot:item.current_daily_bags="{ item }">
            <div class="text-xs-center">
              <template v-if="item.is_detail_row">
                {{ formatNum(item.current_daily_bags) }}
              </template>
              <template v-else>
                {{ formatNum(item.current_daily_bags) }}
                <span v-if="report.elapsed_days"> ({{ formatNum(item.current_accumulated_bags != null && report.elapsed_days ? item.current_accumulated_bags / report.elapsed_days : null) }})</span>
              </template>
            </div>
          </template>
          <template v-slot:item.accumulated_vs_goal="{ item }">
            <div class="text-xs-center">
              <div :class="['text-xs', (item.current_accumulated_bags || 0) < (item.current_accumulated_daily_goal || 0) ? 'red--text' : 'success--text']">
                {{ formatNum(item.current_accumulated_bags) }}
                ({{ formatNum(item.current_accumulated_daily_goal) }})
              </div>
            </div>
          </template>
          <template v-slot:item.goal_and_daily_goal="{ item }">
            <div class="text-xs-center">
              <div :class="['text-xs']">
                {{ formatNum(item.current_goal_percentage != null && report.month_goal ? (item.current_goal_percentage / 100) * report.month_goal : null) }}
                ({{ formatNum(item.current_daily_goal) }})
              </div>
            </div>
          </template>
          <template v-slot:item.current_month_tendency="{ item }">
            <div class="text-xs-center">
              {{ formatNum(has_tendency_data(item) ? calc_tendency_for_item(item) : null) }}
              <div v-if="has_tendency_data(item)" :class="['text-xs', calc_tendency_for_item(item) < Number((item.current_goal_percentage / 100) * (report.month_goal || 0)) ? 'red--text' : 'success--text']">
                <v-icon small
                  :color="calc_tendency_for_item(item) < Number((item.current_goal_percentage / 100) * (report.month_goal || 0)) ? 'error' : 'success'">
                  {{ calc_tendency_for_item(item) / (Number((item.current_goal_percentage / 100) * (report.month_goal || 1))) >= 1 ? 'mdi-menu-up' : 'mdi-menu-down'}}
                </v-icon>
                {{ format_percentage(100*(calc_tendency_for_item(item) / (Number((item.current_goal_percentage / 100) * (report.month_goal || 1)))) - 100) }}%
              </div>
              <div v-else class="text-xs">-</div>
            </div>
          </template>
          <template v-slot:item.last_month_total_display="{ item }">
            <div class="text-xs-center">
              <template v-if="item.last_month_total_ammount != null && item.last_month_total_ammount !== ''">
                {{ formatNum(item.last_month_total_ammount) }}<br>
                <template v-if="has_tendency_data(item)">
                  <v-icon small :color="(item.last_month_total_ammount || 0) > calc_tendency_for_item(item) ? 'mdi-alert-circle' : 'mdi-check'">
                    {{ (item.last_month_total_ammount || 0) > calc_tendency_for_item(item) ? 'mdi-menu-down' : 'mdi-menu-up' }}
                  </v-icon>
                  <div :class="(item.last_month_total_ammount || 0) > calc_tendency_for_item(item) ? 'red--text' : 'success--text'">
                    {{ format_percentage(calc_tendency_for_item(item) / ((item.last_month_total_ammount || 1) || 1) * 100 - 100)}}%
                  </div>
                </template>
              </template>
              <span v-else>-</span>
            </div>
          </template>
          <template v-slot:item.last_year_total_display="{ item }">
            <div class="text-xs-center">
              <template v-if="item.last_year_total != null && item.last_year_total !== ''">
                {{ formatNum(item.last_year_total) }}<br>
                <template v-if="has_tendency_data(item)">
                  <v-icon small :color="(item.last_year_total || 0) > calc_tendency_for_item(item) ? 'error' : 'success'">
                    {{ (item.last_year_total || 0) > calc_tendency_for_item(item) ? 'mdi-menu-down' : 'mdi-menu-up' }}
                  </v-icon>
                  <div :class="(item.last_year_total || 0) > calc_tendency_for_item(item) ? 'red--text' : 'success--text'">
                    {{ format_percentage(calc_tendency_for_item(item) / ((item.last_year_total || 1) || 1) * 100 - 100)}}%
                  </div>
                </template>
              </template>
              <span v-else>-</span>
            </div>
          </template>
        </v-data-table>
        <div v-if="!visibleItems || visibleItems.length === 0 && !loading" class="text-center py-4 text--disabled">
          Nenhum vendedor encontrado com metas consolidadas para o período selecionado.
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'SellerConsolidatedGoals',
  props: {
    sellers: {
      type: Array,
      default: () => { return []}
    },
    report: { 
      type: Object,
      default: () => ({
        month_goal: 0,
        working_days: 1,
        elapsed_days: 1,
      }),
    },
    loading: { 
      type: Boolean,
      default: false,
    },
    date_search: { 
      type: String,
      required: true,
    },
  },
  data() {
    return {
      expandedSellers: [],
      headers: [
        { text: '', value: 'expand', align: 'left', sortable: false, width: '40' },
        { text: 'Vendedor', value: 's_name', align: 'left', sortable: true },
        { text: 'Modalidade', value: 'modality_name', align: 'left', sortable: true },
        { text: 'Venda Hoje (Média Diária)', value: 'current_daily_bags', align: 'center', sortable: false },
        { text: 'Acumulado (Meta Acumulada)', value: 'accumulated_vs_goal', align: 'center', sortable: false },
        { text: 'Objetivo (Meta diária)', value: 'goal_and_daily_goal', align: 'center', sortable: false },
        { text: 'Tendência mês atual', value: 'current_month_tendency', align: 'center', sortable: false },
        { text: 'Venda mês anterior', value: 'last_month_total_display', align: 'center', sortable: false },
        { text: 'Venda mesmo mês, ano anterior', value: 'last_year_total_display', align: 'center', sortable: false },
      ],
    };
  },
  computed: {
    /** Por vendedor: uma linha por modalidade + uma linha total (soma). */
    sellerGroups() {
      const groups = [];
      if (!this.report || !this.sellers) return groups;

      this.sellers.forEach(seller => {
        const modalities = seller.modalities || [];
        const detailRows = modalities.map(modality => ({
          unique_key: `detail-${seller.user_id}-${modality.modality_id}`,
          s_name: seller.s_name,
          user_id: seller.user_id,
          modality_id: modality.modality_id,
          modality_name: modality.modality_name != null && modality.modality_name !== '' ? modality.modality_name : '-',
          is_total_row: false,
          is_detail_row: true,
          ...modality
        }));

        const sum = (key) => modalities.reduce((acc, m) => acc + (Number(m[key]) || 0), 0);
        const totalRow = {
          unique_key: `total-${seller.user_id}`,
          s_name: seller.s_name,
          user_id: seller.user_id,
          modality_name: 'Total',
          is_total_row: true,
          is_detail_row: false,
          current_daily_bags: sum('current_daily_bags'),
          current_accumulated_bags: sum('current_accumulated_bags'),
          current_accumulated_daily_goal: sum('current_accumulated_daily_goal'),
          current_daily_goal: sum('current_daily_goal'),
          current_goal_percentage: sum('current_goal_percentage'),
          last_month_total_ammount: sum('last_month_total_ammount'),
          last_year_total: sum('last_year_total')
        };

        groups.push({ seller, totalRow, detailRows });
      });
      return groups;
    },
    /** Linhas visíveis: total de cada vendedor + detalhes quando expandido. */
    visibleItems() {
      const out = [];
      this.sellerGroups.forEach(({ totalRow, detailRows }) => {
        out.push(totalRow);
        if (this.expandedSellers.includes(totalRow.user_id)) {
          detailRows.forEach(r => out.push(r));
        }
      });
      return out;
    }
  },
  methods: {
    toggleExpand(userId) {
      const i = this.expandedSellers.indexOf(userId);
      if (i >= 0) {
        this.expandedSellers = this.expandedSellers.filter(id => id !== userId);
      } else {
        this.expandedSellers = [...this.expandedSellers, userId];
      }
    },
    formatNum(val) {
      if (val === null || val === undefined || val === '') return '-';
      const n = Number(val);
      if (Number.isNaN(n)) return '-';
      return Math.round(n).toLocaleString('pt-BR');
    },
    format_percentage(number) {
      return Math.min(Math.max(Number(number).toFixed(2), -999.99), 999.99).toLocaleString('pt-BR');
    },
    calc_month_mean_for_item(item) {
      return (Number(item.current_accumulated_bags) / (Number(this.report.elapsed_days) || 1));
    },
    has_tendency_data(item) {
      return this.report && Number(this.report.working_days) >= 0 && Number(this.report.elapsed_days) >= 0 && item && (item.current_accumulated_bags != null && item.current_accumulated_bags !== '');
    },
    calc_tendency_for_item(item) {
      const remaining_days = (this.report.working_days || 0) - (this.report.elapsed_days || 0);
      return (item.current_accumulated_bags || 0) + (remaining_days * this.calc_month_mean_for_item(item));
    },
  },
};
</script>